import { ApexOptions } from 'apexcharts';
import { chartInfoInterface } from 'src/interfaces/Chart';
import { findPower, formatPowerForGraph } from 'src/Resources/formatPower';

export const getChartOptions = (
  chartInfo: chartInfoInterface,
  type: string,
  dateList: string[],
  stacked: Boolean,
  voltageInfo?: chartInfoInterface,
  powerLabel?: string,
  temperatureInfo?: chartInfoInterface,
  // Must match useIsMobile() exactly — theme.breakpoints.down('md') is
  // max-width:959.95px, so 960 itself is desktop. With `<= 960` a 960px-wide
  // window laid out as desktop but got mobile chart options, which blanks the
  // y-axis unit while the desktop title suffix is not rendered: units vanish.
  isMobile: boolean = typeof window !== 'undefined' && window.innerWidth < 960,
  // dailyoverview: how many power series come before Battery SOC (grid, PV and
  // load = 3; Sodistore Grid only has battery power = 1).
  powerSeriesCount: number = 3
): ApexOptions => {
  return type.includes('daily')
    ? {
        chart: {
          id: 'area-datetime',
          toolbar: {
            show: false
          },
          type: 'line',
          height: 350,
          zoom: {
            autoScaleYaxis: false
          }
        },

        dataLabels: {
          enabled: false
        },
        fill: {
          type: 'solid'
        },

        colors: ['#3498db', '#2ecc71', '#282828'],
        xaxis: {
          type: 'datetime',
          // Mobile: fewer ticks + smaller font so the full time axis fits.
          tickAmount: isMobile ? 5 : 8,
          labels: {
            style: {
              fontSize: isMobile ? '10px' : '12px'
            },
            datetimeFormatter: {
              year: 'yyyy',
              month: "MMM 'yy",
              day: 'dd MMM',
              hour: 'HH:mm',
              minute: 'mm'
            }
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        grid: {
          padding: {
            top: 30
          }
        },
        yaxis:
          type === 'dailyoverview'
            ? [
                // One power axis per power series. The first one is visible, the
                // others only exist to keep yaxis[i] paired with series[i].
                ...Array.from({ length: powerSeriesCount }, (_, i) => ({
                  seriesName: powerLabel ?? 'Grid Power',
                  show: i === 0,
                  tickAmount: 6,
                  min:
                    chartInfo.min >= 0
                      ? 0
                      : chartInfo.max <= 0
                      ? Math.ceil(
                          chartInfo.min / findPower(chartInfo.min).value
                        ) * findPower(chartInfo.min).value
                      : undefined,
                  max:
                    chartInfo.min >= 0
                      ? Math.ceil(
                          chartInfo.max / findPower(chartInfo.max).value
                        ) * findPower(chartInfo.max).value
                      : chartInfo.max <= 0
                      ? 0
                      : undefined,
                  title:
                    i === 0
                      ? {
                          text: isMobile ? '' : chartInfo.unit,
                          style: {
                            fontSize: '12px'
                          },
                          offsetY: isMobile ? 0 : -190,
                          offsetX: isMobile ? 0 : 25,
                          rotate: isMobile ? -90 : 0
                        }
                      : undefined,
                  labels: {
                    formatter: function (value: number) {
                      return formatPowerForGraph(
                        value,
                        chartInfo.magnitude
                      ).value.toString();
                    }
                  }
                })),
                {
                  seriesName: 'Battery SOC',
                  opposite: true,
                  // Mobile: hide the right-side unit axes to widen the plot.
                  show: !isMobile,
                  tickAmount: 5,
                  min: 0,
                  max: 100,
                  title: {
                    text: '(%)',
                    style: {
                      fontSize: '12px'
                    },
                    offsetY: -190,
                    offsetX: -25,
                    rotate: 0
                  },
                  labels: {
                    formatter: function (value: number) {
                      return Math.round(value).toString();
                    }
                  }
                },
                ...(voltageInfo ? [{
                  seriesName: 'Battery Voltage',
                  opposite: true,
                  // Mobile: hide the right-side unit axes to widen the plot.
                  show: !isMobile,
                  tickAmount: 5,
                  min: voltageInfo.min > 0 ? Math.floor(voltageInfo.min / 5) * 5 : 0,
                  max: Math.ceil(voltageInfo.max / 5) * 5,
                  title: {
                    text: '(V)',
                    style: {
                      fontSize: '12px'
                    },
                    offsetY: -190,
                    offsetX: -45,
                    rotate: 0
                  },
                  labels: {
                    formatter: function (value: number) {
                      return Math.round(value).toString();
                    }
                  }
                }] : []),
                ...(temperatureInfo ? [{
                  seriesName: 'Battery Temperature',
                  opposite: true,
                  // Mobile: hide the right-side unit axes to widen the plot.
                  show: !isMobile,
                  tickAmount: 5,
                  min: Math.floor((temperatureInfo.min - 5) / 5) * 5,
                  max: Math.ceil((temperatureInfo.max + 5) / 5) * 5,
                  title: {
                    text: '(°C)',
                    style: {
                      fontSize: '12px'
                    },
                    offsetY: -190,
                    offsetX: -65,
                    rotate: 0
                  },
                  labels: {
                    formatter: function (value: number) {
                      return Math.round(value).toString();
                    }
                  }
                }] : [])
              ]
            : {
                tickAmount: chartInfo.unit === '(%)' ? 5 : 6,
                min:
                  chartInfo.min >= 0
                    ? 0
                    : chartInfo.max <= 0
                    ? Math.ceil(
                        chartInfo.min / findPower(chartInfo.min).value
                      ) * findPower(chartInfo.min).value
                    : undefined,
                max:
                  chartInfo.min >= 0
                    ? Math.ceil(
                        chartInfo.max / findPower(chartInfo.max).value
                      ) * findPower(chartInfo.max).value
                    : chartInfo.max <= 0
                    ? 0
                    : undefined,
                title: {
                  text: isMobile ? '' : chartInfo.unit,
                  style: {
                    fontSize: '12px'
                  },
                  offsetY: isMobile ? 0 : -190,
                  offsetX: isMobile ? 0 : 25,
                  rotate: isMobile ? -90 : 0
                },
                labels: {
                  formatter: function (value: number) {
                    if (chartInfo.unit === '(%)') {
                      return Math.round(value).toString();
                    }
                    return formatPowerForGraph(
                      value,
                      chartInfo.magnitude
                    ).value.toString();
                  }
                }
              },

        // Mobile: only the COMBINED overview graph has mixed units (W/%/V) with
        // no single common unit — carry each series' unit in its legend label.
        // Single-unit charts put their shared unit in the section title instead.
        legend:
          isMobile && type === 'dailyoverview'
            ? {
              formatter: function (seriesName: string) {
                const label = (name: string, unit: string) =>
                  name + ' (' + unit + ')';
                if (seriesName === 'Battery SOC') return label(seriesName, '%');
                if (seriesName === 'Battery Voltage')
                  return label(seriesName, 'V');
                if (seriesName === 'Battery Temperature')
                  return label(seriesName, '°C');
                const powerUnit =
                  (chartInfo.unit ?? 'W').replace(/[()]/g, '').trim() || 'W';
                return label(seriesName, powerUnit);
              }
            }
          : {},
        tooltip: {
          shared: true,
          x: {
            format: 'dd MMM HH:mm:ss'
          },
          y: {
            formatter: function (val, { seriesIndex, w }) {
              // `shared: true` calls this for every series at the hovered x,
              // even when a particular series has no data point there → val undefined.
              if (val === undefined || val === null || Number.isNaN(val)) {
                return '-';
              }
              const seriesName = w.config.series[seriesIndex].name;
              if (seriesName === 'Battery SOC') {
                return val.toFixed(2) + ' %';
              } else if (seriesName === 'Battery Voltage') {
                return val.toFixed(2) + ' (V)';
              } else if (seriesName === 'Battery Temperature') {
                return val.toFixed(2) + ' (°C)';
              } else {
                const formatted = formatPowerForGraph(val, chartInfo.magnitude);
                const raw = formatted?.value;
                return (
                  (raw === undefined || raw === null || Number.isNaN(raw)
                    ? '-'
                    : raw.toFixed(2)) +
                  ' ' +
                  (chartInfo.unit ?? '')
                );
              }
            }
          }
        }
      }
    : {
        chart: {
          height: 380,
          stacked: chartInfo.unit !== '(%)' && type != 'overview'
        },
        stroke: {
          width: [0, 0, 0, 2],
          curve: 'smooth'
        },
        plotOptions: {
          bar: {
            //If min<0 and max>0, then we have 2 stacked bars, one with positive and one with negative values, so each one of them should have border radius. \
            //Otherwise, we have a single bar or two stacked bars either with positive or negative values.
            borderRadius:
              stacked && chartInfo.min <= 0 && chartInfo.max > 0 ? [10] : 10,
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: false,
          formatter: function (val) {
            return (
              formatPowerForGraph(val, chartInfo.magnitude).value.toFixed(2) +
              ' ' +
              chartInfo.unit
            );
          },
          offsetY: -20,
          style: {
            fontSize: '12px',
            colors: ['#304758']
          }
        },

        xaxis: {
          categories: dateList,
          position: 'bottom',

          axisBorder: {
            show: false
          },
          axisTicks: {
            show: false
          },
          tooltip: {
            enabled: true
          }
        },
        yaxis: {
          tickAmount: 6,
          min:
            chartInfo.min >= 0
              ? 0
              : chartInfo.max <= 0
              ? Math.ceil(chartInfo.min / findPower(chartInfo.min).value) *
                findPower(chartInfo.min).value
              : Math.abs(chartInfo.min) < 1 || Math.abs(chartInfo.max) < 1
              ? -Math.max(
                  Math.abs(
                    Math.ceil(chartInfo.min / findPower(chartInfo.min).value) *
                      findPower(chartInfo.min).value
                  ),
                  Math.ceil(chartInfo.max / findPower(chartInfo.max).value) *
                    findPower(chartInfo.max).value
                ).toFixed(2)
              : undefined,
          max:
            chartInfo.min >= 0
              ? +(
                  Math.ceil(chartInfo.max / findPower(chartInfo.max).value) *
                  findPower(chartInfo.max).value
                ).toFixed(2)
              : chartInfo.max <= 0
              ? 0
              : Math.abs(chartInfo.min) < 1 || Math.abs(chartInfo.max) < 1
              ? +Math.max(
                  Math.abs(
                    Math.ceil(chartInfo.min / findPower(chartInfo.min).value) *
                      findPower(chartInfo.min).value
                  ),
                  Math.ceil(chartInfo.max / findPower(chartInfo.max).value) *
                    findPower(chartInfo.max).value
                ).toFixed(2)
              : undefined,
          title: {
            text: isMobile ? '' : chartInfo.unit,
            style: {
              fontSize: '12px'
            },
            offsetY: isMobile ? 0 : type === 'monthly' ? -176 : -178,
            offsetX: isMobile ? 0 : 25,
            rotate: isMobile ? -90 : 0
          },
          labels: {
            formatter: function (value: number) {
              return formatPowerForGraph(value, 0).value.toString();
            }
          }
        },
        annotations: {
          yaxis: [
            {
              y: 0,
              strokeDashArray: 0,
              borderColor: '#d3d3d3',
              borderWidth: 1,
              yAxisIndex: 0
            }
          ]
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val < 1
                ? val.toFixed(3) + ' ' + chartInfo.unit
                : val.toFixed(2) + ' ' + chartInfo.unit;
            }
          }
        }
      };
};
